import type { BaseServiceOptions, Pagination, TPayment } from '@/core/types'
import {
  TPayments,
  modulePaymentServiceHandlers,
  serviceConfig,
} from '../../..'
import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'

const { model } = serviceConfig.payments

export const usePaymentsQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, enableQuery, filters, modelId, handlers } = options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TPayment>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const getPaymentPercentage = (total: number, quantity: number) => {
    if (!total) return 0

    const result = (quantity / total) * 100

    return result.toFixed(2)
  }

  const deleteRecord = useMutation<void, AxiosError, TPayment>(
    (payload) =>
      modulePaymentServiceHandlers.deleteRecord({
        modelChildId: payload.id,
        modelId,
      }),
    {
      onSuccess: (_, payload) => serviceHandlers.deleteRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TPayment, AxiosError, TPayment>(
    (payload: TPayment) =>
      modulePaymentServiceHandlers.addRecord(payload, { modelId }),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TPayments, AxiosError, TPayments>({
    queryKey: [model, currentPage, filters?.loadRecords],
    queryFn: () =>
      modulePaymentServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
        modelId,
      }),
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data?.meta ?? defaultPagination)
      }
    },
    select: (data) => ({
      ...data,
      data: data.data.map((record) => ({
        ...record,
        total_paid_percentage: getPaymentPercentage(
          record.total_purchase_order ?? 0,
          record.amount ?? 0,
        ),
      })),
    }),
    enabled: enableQuery?.loadRecords,
    refetchOnWindowFocus: false,
  })

  return {
    loadRecords,
    addRecord,
    deleteRecord,
  }
}
